// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { CustomerProductList, CustomerProductListResult } from '~/lib/data-contract/salesforce';
import { useCommerceAPI } from '../contexts';
import { handleAsyncError } from '../utils';
import { useCustomerStore } from './useCustomerStore';

/**
 * This hook is designed to add customer product list capabilities
 * to your app, it leverages the Commerce API - Shopper Customer endpoints.
 * It uses a React context to store customer product list globally.
 *
 * By default, the Shopper Customer Product List API allows a shopper to
 * save multiple product lists. However, the PWA only use a single
 * product list, which is the wishlist. There is another hook useWishlist,
 * which is built on top of this hook to add wishlist specific logic.
 *
 * This Hook only works when your components are wrapped in CustomerProductListProvider.
 */
export default function useCustomerProductLists() {
    const api = useCommerceAPI();
    const { customer } = useCustomerStore((state) => ({
        customer: state.customer,
    }));

    const getLists = handleAsyncError(() => {
        return api.shopperCustomers?.getCustomerProductLists({
            parameters: {
                customerId: customer.customerId as string,
            },
        });
    });

    const createList = handleAsyncError((name, type) => {
        return api.shopperCustomers?.createCustomerProductList({
            body: {
                type,
                name,
            },
            parameters: {
                customerId: customer.customerId as string,
            },
        });
    });

    const getList = handleAsyncError((listId) => {
        return api.shopperCustomers?.getCustomerProductList({
            parameters: {
                customerId: customer.customerId as string,
                listId,
            },
        });
    });

    const createListItem = handleAsyncError((listId, item) => {
        const { id, quantity = 1 } = item;
        return api.shopperCustomers?.createCustomerProductListItem({
            body: {
                productId: id,
                quantity,
                public: false,
                priority: 1,
                type: 'product',
            },
            parameters: {
                customerId: customer.customerId as string,
                listId,
            },
        });
    });

    const updateListItem = handleAsyncError((listId, item) => {
        const { id, quantity } = item;
        return api.shopperCustomers?.updateCustomerProductListItem({
            body: {
                id,
                quantity,
                public: false,
                priority: 1,
            },
            parameters: {
                customerId: customer.customerId as string,
                listId: listId,
                itemId: item.id,
            },
        });
    });

    const removeListItem = handleAsyncError((listId, itemId) => {
        return api.shopperCustomers?.deleteCustomerProductListItem(
            {
                parameters: {
                    itemId,
                    listId,
                    customerId: customer.customerId as string,
                },
            },
            true,
        );
    });

    const getOrCreateList = async (name, type, data?: CustomerProductListResult) => {
        let list;
        let created = false;
        try {
            // Note: if list is empty, the API response
            // does NOT contain the "data" key.
            list = data?.data?.find((list) => list.name === name && list.type === type);
            if (!list) {
                created = true;
                try {
                    list = (await createList(name, type)) as CustomerProductList;
                } catch (e) {
                    throw new Error(e);
                }
            }

            return { ...list, created };
        } catch (e) {
            throw new Error(e);
        }
    };

    return {
        getLists,
        createList,
        getList,
        createListItem,
        updateListItem,
        removeListItem,
        getOrCreateList,
    };
}
